// apps/web/src/components/shell/menu-avatar.tsx — Fase 17.8, Plano 09 (D-16; UI-SPEC U-10, T-17.8-09-01).
// Menu do avatar no rodapé da sidebar: nome e perfil, tema claro/escuro e Sair. Substitui o avatar, o badge de papel e o
// botão Sair que viviam na topbar. O Plano 15 acrescenta o item "Atalhos de teclado" (abre a folha de atalhos).
import { useState, type ReactElement } from 'react';
import { Keyboard, LogOut, Moon, Sun } from 'lucide-react';
import type { SessaoAtualResponse } from '@pieltec/contracts';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useSidebar } from '../ui/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { apiMutate } from '../../lib/csrf-fetch';
import { useAppearance } from './appearance';

/** Permissão que identifica a pessoa administradora; o rótulo é ergonomia, a autorização real é do servidor. */
const PERMISSAO_ADMINISTRATIVA = 'users:manage';

export interface MenuAvatarProps {
  readonly usuario: SessaoAtualResponse | null;
  /** Abre a folha de atalhos (o estado vive no AppShell). */
  readonly onAbrirAtalhos?: () => void;
}

/** Perfil exibido sob o nome: "Administrador" para quem gerencia usuários, senão o papel da sessão. */
export function rotuloDoPerfil(usuario: SessaoAtualResponse | null): string {
  if (!usuario) return '';
  return usuario.permissions.includes(PERMISSAO_ADMINISTRATIVA) ? 'Administrador' : usuario.role;
}

async function sair(): Promise<void> {
  try {
    await apiMutate('/api/v1/auth/logout', { method: 'POST' });
  } catch {
    // Mesmo em falha, força o redirecionamento; o token CSRF vai na chamada, então a sessão é de fato revogada.
  } finally {
    if (typeof window !== 'undefined') {
      window.location.assign('/login');
    }
  }
}

export function MenuAvatar({ usuario, onAbrirAtalhos }: MenuAvatarProps): ReactElement {
  const { state, isMobile } = useSidebar();
  const { theme, toggle } = useAppearance();
  const [aberto, setAberto] = useState(false);
  const noRail = !isMobile && state === 'collapsed';
  const nome = usuario?.displayName || usuario?.username || 'Carregando…';
  const perfil = rotuloDoPerfil(usuario);
  const inicial = (usuario?.displayName || usuario?.username || '?').charAt(0).toUpperCase();
  const rotuloTema = theme === 'mineral' ? 'Tema escuro' : 'Tema claro';

  const gatilho = (
    <button type="button" className="menu-avatar__gatilho" data-testid="menu-avatar-trigger" aria-label={`Menu da conta: ${nome}`}>
      <Avatar className="menu-avatar__avatar">
        <AvatarFallback className="menu-avatar__inicial">{inicial}</AvatarFallback>
      </Avatar>
      <span className="menu-avatar__texto">
        <span className="menu-avatar__nome">{nome}</span>
        {perfil && <span className="menu-avatar__perfil">{perfil}</span>}
      </span>
    </button>
  );

  return (
    <DropdownMenu open={aberto} onOpenChange={setAberto}>
      {noRail ? (
        <Tooltip>
          <TooltipTrigger asChild><DropdownMenuTrigger asChild>{gatilho}</DropdownMenuTrigger></TooltipTrigger>
          <TooltipContent side="right" sideOffset={8} hidden={aberto}>{perfil ? `${nome}, ${perfil}` : nome}</TooltipContent>
        </Tooltip>
      ) : (
        <DropdownMenuTrigger asChild>{gatilho}</DropdownMenuTrigger>
      )}
      <DropdownMenuContent side="top" align="start" sideOffset={8} className="menu-avatar__menu" data-testid="menu-avatar-conteudo">
        <DropdownMenuLabel className="menu-avatar__cabecalho">
          <span className="menu-avatar__cabecalho-nome">{nome}</span>
          {perfil && <span className="menu-avatar__cabecalho-perfil">{perfil}</span>}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => onAbrirAtalhos?.()} data-testid="menu-avatar-atalhos" className="cursor-pointer">
          <Keyboard className="size-4" aria-hidden="true" />
          <span>Atalhos de teclado</span>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={toggle} data-testid="menu-avatar-tema" className="cursor-pointer">
          {theme === 'mineral' ? <Moon className="size-4" aria-hidden="true" /> : <Sun className="size-4" aria-hidden="true" />}
          <span>{rotuloTema}</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void sair()} data-testid="logout-button" className="cursor-pointer">
          <LogOut className="size-4" aria-hidden="true" />
          <span>Sair</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
